import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { useMixin } from '@clds/component-theme';
import { TypographySize } from '@clds/typography';
import { ButtonSize } from '@clds/button';
import { CopyButton } from './components/CopyButton';
import { HorizontalToolbar, StyledCode } from './CodeSnippet.styled';
import { ResponsiveHeader } from './components/ResponsiveHeader';
import { CodeSnippetProps, VersionedSnippet, VersionName } from './types';
import { useActiveSnippet, useActiveSnippetVersion, useSortSnippets } from './hooks';
import { CodeSnippetLayout } from './components/CodeSnippetLayout';
import { SnippetVersionPicker } from './components/SnippetVersionsPicker';
import { t } from './CodeSnippetTheme';

export interface VersionedCodeSnippetProps extends CodeSnippetProps<VersionedSnippet> {
  onLangVersionChange?: (version: VersionName) => void;
  langVersion?: string;
  extraHeaderButtons?: ({ buttonSize }: { buttonSize?: ButtonSize }) => ReactNode;
  footer?: ReactNode;
}

export const VersionedCodeSnippet = styled<FC<VersionedCodeSnippetProps>>(
  ({
    snippets,
    activeLanguage,
    onLanguageChange,
    langVersion,
    onLangVersionChange,
    onCopy,
    parsedUrl,
    height,
    headerSize = 'sm',
    extraHeaderButtons = () => null,
    footer,
    ...rest
  }) => {
    const sortedSnippets = useSortSnippets<VersionedSnippet>(snippets);
    const activeSnippet = useActiveSnippet(sortedSnippets, activeLanguage);
    const activeSnippetVersion = useActiveSnippetVersion(langVersion, activeSnippet);
    const tabsSize = useMixin(t.header.sizeVariant[headerSize].typographySize) as TypographySize;
    const buttonSize = useMixin(t.header.sizeVariant[headerSize].buttonSize) as ButtonSize;

    const header = (
      <ResponsiveHeader
        snippets={sortedSnippets}
        activeSnippet={activeSnippet}
        onSnippetChange={onLanguageChange}
        tabsSize={tabsSize}
        buttonSize={buttonSize}
      >
        <HorizontalToolbar>
          <SnippetVersionPicker
            versions={activeSnippet.versions}
            activeVersion={activeSnippetVersion?.versionName}
            onVersionChange={onLangVersionChange}
            tone="plain"
            size={buttonSize}
          />
          {extraHeaderButtons({ buttonSize })}
          <CopyButton onCopy={onCopy} contentToCopy={activeSnippetVersion.snippet} tone="plain" size={buttonSize} />
        </HorizontalToolbar>
      </ResponsiveHeader>
    );

    return (
      <CodeSnippetLayout header={header} {...rest}>
        <>
          <StyledCode syntax={activeSnippetVersion.syntax ?? activeSnippet.syntax} parsedUrl={parsedUrl} height={height}>
            {activeSnippetVersion.snippet}
          </StyledCode>
          {footer}
        </>
      </CodeSnippetLayout>
    );
  }
)``;
